import '@hotwired/turbo-rails'
import '@hotwired/stimulus'
import '@algolia/autocomplete-theme-classic/dist/theme.css'
import 'bootstrap'
import { closeModal } from '../src/utils/close-modal'
import { exceptions } from '../src/utils/exceptions'
import { hydrateReactComponents } from '../src/utils/react/hydrate'
import {
  unmountReactComponents,
  unmountReactComponentsInFrame,
} from '../src/utils/react/unmount-components'
import { loadBootstrapIntoStore } from '../src/api/fetch-bootstrap'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { BootstrapErrorBoundary } from '../src/components/bootstrap-error-boundary'
import { errorToast } from '../src/utils/error-toast'
import '../stylesheets/main.scss'
import 'app/javascript/controllers'
import 'app/assets/stylesheets/combobox.css'
import 'app/assets/stylesheets/position_alias.css'

const isDevelopment = process.env.NODE_ENV === 'development'

/**
 * Conditional logging - only in development
 */
const logBootstrap = (message: string, data?: unknown) => {
  if (isDevelopment) {
    console.log(`[Bootstrap] ${message}`, data || '')
  }
}

if (typeof window !== 'undefined') {
  exceptions.setup()

  window.Turbo.session.drive = true

  window.closeModal = closeModal
  hydrateReactComponents()

  window.hydrateReactComponents = hydrateReactComponents
  window.$(document).on('modal:loaded', () => {
    hydrateReactComponents({ root: '#modal-anchor' })
  })
  document.addEventListener('turbo:load', () => {
    hydrateReactComponents()
  })

  document.addEventListener('turbo:frame-render', () => {
    hydrateReactComponents()
  })

  document.addEventListener('turbo:before-render', unmountReactComponents)

  // turbo:before-render only fires on full-page Turbo Drive visits. Side-nav
  // links navigate by frame (data-turbo-frame="content" in
  // components/side-menu/link-item.tsx), which fires
  // turbo:before-frame-render instead — without this listener the React
  // roots in the markup the frame replaces are never released.
  document.addEventListener(
    'turbo:before-frame-render',
    unmountReactComponentsInFrame,
  )

  document.addEventListener('turbo:frame-missing', (event) => {
    const {
      detail: { response, visit },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } = event as any

    event.preventDefault()
    visit(response.url)
  })

  // Wait for userId to be available (it's set in jQuery's document.ready)
  const waitForUserId = async (maxAttempts = 50): Promise<string | null> => {
    for (let i = 0; i < maxAttempts; i++) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const userId = (window as any).currentUserID
      if (userId) {
        logBootstrap('userId found', userId)
        return userId
      }
      // Wait 50ms before checking again
      await new Promise((resolve) => setTimeout(resolve, 50))
    }
    logBootstrap('userId not found after waiting')
    return null
  }

  // Bootstrap function to avoid duplication
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBootstrap = async (store: any) => {
    const bootstrapStartTime = performance.now()
    logBootstrap('Starting bootstrap process')

    // Add breadcrumb for bootstrap start
    exceptions.addBreadcrumb({
      action: 'Bootstrap process started',
      category: 'Bootstrap',
      metadata: {
        timestamp: new Date().toISOString(),
        bootstrapping: store.bootstrapping,
      },
    })

    // Wait for userId to be set (it's set in jQuery's document.ready)
    const userId = await waitForUserId()
    logBootstrap('userId retrieved', {
      userId,
      bootstrapping: store.bootstrapping,
    })

    // Add breadcrumb for userId retrieval
    if (userId) {
      exceptions.addBreadcrumb({
        action: 'UserId retrieved',
        category: 'Bootstrap',
        metadata: {
          userId,
        },
      })
    }

    if (userId) {
      try {
        logBootstrap('Starting loadBootstrapIntoStore...')
        const bootstrapData = await loadBootstrapIntoStore(store)
        logBootstrap(
          'loadBootstrapIntoStore completed, starting store.bootstrap...',
        )

        // Await bootstrap to ensure it completes before continuing
        // This ensures completeBootstrapping() is called even if bootstrap fails
        await store.bootstrap(bootstrapData.userId, bootstrapData.orgId || null)

        const totalBootstrapTime = performance.now() - bootstrapStartTime
        logBootstrap(
          `Bootstrap completed successfully in ${totalBootstrapTime.toFixed(
            2,
          )}ms`,
        )

        // Add breadcrumb for successful bootstrap completion
        const successMetadata: Record<string, string | number> = {
          userId: bootstrapData.userId,
          totalTime: parseFloat(totalBootstrapTime.toFixed(2)),
        }
        if (bootstrapData.orgId) {
          successMetadata.orgId = bootstrapData.orgId
        }
        exceptions.addBreadcrumb({
          action: 'Bootstrap completed successfully',
          category: 'Bootstrap',
          metadata: successMetadata,
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const errorTime = performance.now() - bootstrapStartTime
        const errorMessage = error?.message || 'Unknown error'

        const errorObj =
          error instanceof Error ? error : new Error(String(error))
        exceptions.handleCustom('BootstrapProcessError', {
          message: errorObj.message,
          params: {
            userId,
            errorName: errorObj.name,
            errorStack: errorObj.stack,
            errorStatus: error?.status,
            bootstrapTime: `${errorTime.toFixed(2)}ms`,
            storeBootstrapping: store.bootstrapping,
          },
          tags: {
            error_type: 'bootstrap_process',
            user_id: userId || 'unknown',
            http_status: error?.status?.toString() || 'unknown',
          },
        })

        // Show user-friendly error message
        if (error?.status === 401) {
          errorToast(
            'Your session has expired. Please refresh the page to log in again.',
          )
        } else if (error?.status === 500) {
          errorToast(
            'Server error occurred. Please try again or contact support.',
          )
        } else if (errorMessage.includes('Failed to fetch')) {
          errorToast(
            'Unable to load application data. Please check your connection and try again.',
          )
        } else {
          errorToast(
            'Failed to load application. Please refresh the page or contact support if the problem persists.',
          )
        }

        logBootstrap(
          `Failed to bootstrap store after ${errorTime.toFixed(2)}ms`,
          error,
        )

        // Ensure bootstrapping is marked as complete even on error
        // This prevents the page from being stuck on the loading screen
        try {
          store.clear()
        } catch (clearError) {
          exceptions.handle(
            clearError instanceof Error
              ? clearError
              : new Error(String(clearError)),
            {
              params: {
                context: 'bootstrap_error_recovery',
                originalError: errorMessage,
              },
              tags: {
                error_type: 'bootstrap_clear_store',
              },
            },
          )
          logBootstrap('Failed to clear store', clearError)
        }
        store.completeBootstrapping()
        logBootstrap('Bootstrapping marked as complete after error')
      }
    } else {
      logBootstrap('No userId, clearing store and completing bootstrapping')
      store.clear()
      store.completeBootstrapping()
    }
  }

  let bootstrapPromise: Promise<void> | null = null

  const runBootstrapSafely = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    store: any,
    source: string,
  ): Promise<void> => {
    if (!store) return

    if (bootstrapPromise) {
      logBootstrap(
        `Bootstrap already in progress, skipping duplicate trigger`,
        {
          source,
        },
      )
      return bootstrapPromise
    }

    bootstrapPromise = (async () => {
      try {
        logBootstrap(`Running bootstrap`, { source })
        await handleBootstrap(store)
      } finally {
        bootstrapPromise = null
      }
    })()

    return bootstrapPromise
  }

  // Handle bootstrap loading when store is ready
  document.addEventListener('storeLoaded', async (e: Event) => {
    logBootstrap('storeLoaded event received')
    const customEvent = e as CustomEvent
    const store = customEvent.detail
    await runBootstrapSafely(store, 'storeLoaded')
  })

  // If store already exists (race condition), handle it immediately
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).store && (window as any).store.bootstrapping) {
    logBootstrap('Store already exists, handling bootstrap immediately')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    void runBootstrapSafely((window as any).store, 'store-already-exists')
  }

  document.addEventListener('turbo:load', async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const store = (window as any).store
    if (!store) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentUserID = (window as any).currentUserID
    const storeHasUser = store.currentUser != null
    const isBootstrapping = store.bootstrapping || bootstrapPromise !== null

    logBootstrap('turbo:load bootstrap health check', {
      currentUserID,
      storeHasUser,
      isBootstrapping,
    })

    if (currentUserID && !storeHasUser && !isBootstrapping) {
      exceptions.addBreadcrumb({
        action: 'Bootstrap recovery triggered on turbo:load',
        category: 'Bootstrap',
        metadata: {
          currentUserID,
        },
      })

      await runBootstrapSafely(store, 'turbo:load-recovery')
    }
  })
}
